import { Component } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import POS from "./pages/POS";
import Sales from "./pages/Sales";
import Returns from "./pages/Returns";
import Products from "./pages/Products";
import Stock from "./pages/Stock";
import Purchases from "./pages/Purchases";
import Customers from "./pages/Customers";
import Suppliers from "./pages/Suppliers";
import Documents from "./pages/Documents";
import Expenses from "./pages/Expenses";
import Cash from "./pages/Cash";
import Credits from "./pages/Credits";
import Debts from "./pages/Debts";
import Inventory from "./pages/Inventory";
import Reports from "./pages/Reports";
import Users from "./pages/Users";
import Audit from "./pages/Audit";
import SettingsPage from "./pages/Settings";
import Admin from "./pages/Admin";
import "./App.css";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-10 w-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

class ErrorBoundary extends Component {
  constructor(p) { super(p); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background text-foreground p-6" data-testid="error-boundary">
          <h1 className="font-heading text-xl font-bold">Une erreur est survenue</h1>
          <p className="text-sm text-muted-foreground max-w-md text-center">{String(this.state.error?.message || this.state.error)}</p>
          <button data-testid="error-home" onClick={() => { window.location.href = "/"; }}
            className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-semibold">Retour au tableau de bord</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function RequireAuth({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.platform_role === "superadmin") return <Navigate to="/admin" replace />;
  if (!user.tenant_id) return <Navigate to="/finaliser-inscription" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.platform_role !== "superadmin") return <Navigate to="/" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (user) return <Navigate to={user.platform_role === "superadmin" ? "/admin" : "/"} replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <ErrorBoundary>
        <AppRouter />
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}

function AppRouter() {
  return (
    <Routes>
          <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
          <Route path="/mot-de-passe-oublie" element={<GuestOnly><ForgotPassword /></GuestOnly>} />
          <Route path="/reinitialiser" element={<GuestOnly><ResetPassword /></GuestOnly>} />
          <Route path="/admin" element={<RequireAdmin><Admin /></RequireAdmin>} />
          <Route element={<RequireAuth><Layout /></RequireAuth>}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/pos" element={<POS />} />
            <Route path="/ventes" element={<Sales />} />
            <Route path="/retours" element={<Returns />} />
            <Route path="/produits" element={<Products />} />
            <Route path="/stock" element={<Stock />} />
            <Route path="/achats" element={<Purchases />} />
            <Route path="/clients" element={<Customers />} />
            <Route path="/fournisseurs" element={<Suppliers />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/depenses" element={<Expenses />} />
            <Route path="/caisse" element={<Cash />} />
            <Route path="/creances" element={<Credits />} />
            <Route path="/dettes" element={<Debts />} />
            <Route path="/inventaire" element={<Inventory />} />
            <Route path="/rapports" element={<Reports />} />
            <Route path="/utilisateurs" element={<Users />} />
            <Route path="/audit" element={<Audit />} />
            <Route path="/parametres" element={<SettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}
